import { ChangeDetectionStrategy, Component, computed, inject, Signal } from '@angular/core';
import { TableConfig } from '@shared/components/table-implementation/types/table-config';
import { MatChipOption, MatChipSelectionChange, MatChipsModule } from "@angular/material/chips";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { NgTemplateOutlet } from '@angular/common';
import { IconModule } from "@ogs-gmbh/ngx-m3-components";
import { SideMenuGroupComponent } from '@shared/components/side-menu/components/side-menu-group/side-menu-group.component';
import { SideMenuGroupTitleComponent } from '@shared/components/side-menu/components/side-menu-group/components/side-menu-group-header/side-menu-group-title.component';
import { SideMenuGroupContentComponent } from '@shared/components/side-menu/components/side-menu-group/components/side-menu-group-content/side-menu-group-content.component';
import { SideMenuContentComponent } from "@shared/components/side-menu/components/side-menu-content/side-menu-content.component";
import { LocalizedDatepickerDirective } from '@shared/directives/localized-datepicker/localized-datepicker.directive';
import { TranslationPipeModule } from '@ogs-gmbh/ngx-translate';
import { toSignal } from '@angular/core/rxjs-interop';
import { TABLE_FILTER_GROUP_TYPES, TableFilterGroup } from '@shared/components/table-implementation/types/filter/table-filter-group.type';
import { findFilterByType, TABLE_FILTER_TYPES, TableFilter } from '@shared/components/table-implementation/types/filter/table-filter.type';
import { TableConfigService } from '@core/services/table/table-config.service';

@Component({
  selector: 'app-overview-settings-menu-filter',
  standalone: true,
  imports: [
    SideMenuGroupComponent,
    SideMenuGroupTitleComponent,
    SideMenuGroupContentComponent,
    MatChipOption,
    MatChipsModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    NgTemplateOutlet,
    IconModule,
    SideMenuContentComponent,
    LocalizedDatepickerDirective,
    TranslationPipeModule
  ],
  templateUrl: './overview-settings-menu-filter.component.html',
  styleUrl: './overview-settings-menu-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OverviewSettingsMenuFilterComponent {
  private _tableConfigService: TableConfigService = inject(TableConfigService);


  // eslint-disable-next-line @unicorn/consistent-function-scoping
  protected tableFilterGroups: Signal<TableFilterGroup[]> = computed(() => (
    this._selectedTableConfig()?.filterConfig?.filterGroups
    // eslint-disable-next-line @tseslint/no-non-null-assertion
      ? this._selectedTableConfig()!.filterConfig!.filterGroups
      : []));

  private _selectedTableConfig: Signal<TableConfig | undefined> = toSignal(this._tableConfigService.selectedConfig$);

  protected filterGroupTypes: typeof TABLE_FILTER_GROUP_TYPES = TABLE_FILTER_GROUP_TYPES;

  protected filterTypes: typeof TABLE_FILTER_TYPES = TABLE_FILTER_TYPES;


  protected findFilterByType: typeof findFilterByType = findFilterByType;


  public onDateChange (filter: TableFilter, newDate: Date | undefined): void {
    if (!this._selectedTableConfig()) return;
    // Todo dont reassert filter here

    if (filter.filterValue.filterType === this.filterTypes.START_DATE || filter.filterValue.filterType === this.filterTypes.END_DATE) {
      filter.filterValue.date = newDate;
      // eslint-disable-next-line @tseslint/no-non-null-assertion
      this._tableConfigService.patchFilterConfigLocally(this._selectedTableConfig()!.filterConfig!);
    }
  }

  public onSelectChange (filter: TableFilter, selectionEvent: MatChipSelectionChange, filterGroup?: TableFilterGroup): void {
    const tableConfig: TableConfig | undefined = this._selectedTableConfig();

    if (!tableConfig?.filterConfig || filter.filterValue.filterType !== this.filterTypes.SELECT) return;

    if (filterGroup && filterGroup.groupType === TABLE_FILTER_GROUP_TYPES.SINGLE_SELECT) {
      for (const child of filterGroup.children) {
        if (child.filterValue.filterType === TABLE_FILTER_TYPES.SELECT && child.selector !== filter.selector)
          child.filterValue.isSelected = false;
      }
    }

    filter.filterValue.isSelected = selectionEvent.selected;
    this._tableConfigService.patchFilterConfigLocally(tableConfig.filterConfig);
  }
}
